import moment, { Moment } from 'moment-timezone';
// The /locale-time subpath, not the root: the root pulls @hqo/ui-atoms' React components in, and these
// helpers are only published on the subpath. eslint-import-resolver-node predates `exports` maps and
// cannot see it; tsc, jest and rsbuild all resolve it, so the rule is disabled for this line only.
// eslint-disable-next-line import/no-unresolved
import { applyLocaleTimeConventions, formatLocaleTimeRange } from '@hqo/date-time/locale-time';

import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useLocale } from 'shared/hooks/use-locale';
import { useTimeZone } from 'shared/hooks/use-timezone';

export type DateTime = string | Date | Moment;
export type DateTimeFormatter = (dateTime: DateTime | undefined) => string;
type DateConverter = (dateTime?: DateTime) => Moment | null;

interface DateTimeFormatters {
  formatNumericDate: DateTimeFormatter;
  formatDate: DateTimeFormatter;
  formatTime: DateTimeFormatter;
  formatTimeRange: DateTimeRangeFormatter;
}

export type DateTimeRangeFormatter = (start: DateTime | undefined, end: DateTime | undefined) => string;

interface DateTimeFormats {
  timeFormat?: Intl.DateTimeFormatOptions;
  dateFormat?: Intl.DateTimeFormatOptions;
}

export const useDateConverter = (): DateConverter => {
  const timeZone = useTimeZone();

  return useCallback(
    (dateTime?: DateTime): Moment | null => {
      return dateTime ? moment(dateTime).tz(timeZone) : null;
    },
    [timeZone],
  );
};

export const useDateFormatter = (format?: Intl.DateTimeFormatOptions): DateTimeFormatter => {
  const timeZone = useTimeZone();
  const locale = useLocale();

  return useCallback(
    (dateTime?: DateTime): string => {
      if (!dateTime) {
        return '';
      }

      const formatter = new Intl.DateTimeFormat(locale, { timeZone, ...format });

      if (dateTime instanceof Date || typeof dateTime === 'string') {
        return formatter.format(new Date(dateTime));
      }

      // dateTime might be moment
      return formatter.format(dateTime.toDate());
    },
    [format, locale, timeZone],
  );
};

/**
 * `timeStyle: 'short'` gives CLDR's time for the locale, but French at HqO writes `09 h 00` rather than
 * CLDR's `09:00` — a house convention the shared package owns, keyed by LANGUAGE so fr-FR and fr-CA
 * both get it. Without applyLocaleTimeConventions a French reader saw `09:00`.
 */
export const useTimeFormatter = (format = { timeStyle: 'short' } as Intl.DateTimeFormatOptions): DateTimeFormatter => {
  const formatDateTime = useDateFormatter(format);
  const locale = useLocale();

  return useCallback(
    (dateTime?: DateTime): string => applyLocaleTimeConventions(formatDateTime(dateTime), locale),
    [formatDateTime, locale],
  );
};

/**
 * A time RANGE, via Intl.formatRange rather than `${start} - ${end}`. Hand-composition loses two things
 * CLDR supplies: the locale's own separator (an en dash, not a hyphen) and the designator factored out
 * across the pair — German is `09:00–17:00 Uhr`, one `Uhr` for the range, which concatenation cannot
 * produce.
 */
export const useTimeRangeFormatter = (): DateTimeRangeFormatter => {
  const timeZone = useTimeZone();
  const locale = useLocale();
  const formatTime = useTimeFormatter();

  return useCallback(
    (start?: DateTime, end?: DateTime): string => {
      if (!start) {
        return '';
      }

      if (!end) {
        return formatTime(start);
      }

      const toDate = (value: DateTime): Date => {
        if (value instanceof Date) {
          return value;
        }

        return typeof value === 'string' ? new Date(value) : value.toDate();
      };

      return formatLocaleTimeRange(toDate(start), toDate(end), locale, timeZone);
    },
    [formatTime, locale, timeZone],
  );
};

export const useDateTimeFormatters = (formats?: DateTimeFormats): DateTimeFormatters => {
  const formatNumericDate = useDateFormatter();

  const formatDate = useDateFormatter(formats?.dateFormat);
  const formatTime = useTimeFormatter(formats?.timeFormat);
  const formatTimeRange = useTimeRangeFormatter();

  return {
    formatDate,
    formatNumericDate,
    formatTime,
    formatTimeRange,
  };
};

export const isCertainTime = (dateTime: Moment | null, hours: number, minutes: number): boolean => {
  return dateTime?.hour() === hours && dateTime?.minutes() === minutes;
};

export const useVisitTime = (
  arrivalDate?: Date,
  departureDate?: Date,
  arrivalTime?: string,
  departureTime?: string,
): string[] => {
  const convertDate = useDateConverter();
  const { formatMessage } = useIntl();
  const arrivalMoment = convertDate(arrivalDate);
  const departureMoment = convertDate(departureDate);

  const timeVisit = isCertainTime(departureMoment, 23, 59)
    ? [`${formatMessage({ id: 'labels.from' })} ${arrivalTime}`]
    : [arrivalTime, departureTime];

  return isCertainTime(arrivalMoment, 0, 0) && isCertainTime(departureMoment, 23, 59)
    ? [formatMessage({ id: 'labels.allDayVisit' })]
    : timeVisit;
};
